import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    DoppeActionCTAType,
    doppeActionCtaButtonsResetSettingsForLock,
    DoppeLinkSpecEntityType,
    DoppeLinkSpecType
} from '../../client-server-common/types/doppe-action-cta-button';
import type {PartiallyOptional} from '@wix/devzai-utils-common';
import {
    arrayFlatten,
    arrayRemoveNullValues,
    arraySkipTake,
    asArray,
    hideableValueCreateHidden,
    hideableValueGetVisibleValue,
    hideableValueIsVisibleAndNotEqualValue,
    immerProduce,
    iterableMapToArray
} from '@wix/devzai-utils-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsSpecialContentSettingMetadata,
    DoppeActionLayout,
    DoppeBusinessEntity,
    doppeBusinessEntityDtoIsNotFound,
    DoppeDtoBookingService,
    DoppeExternalServiceProvider,
    doppeExternalServiceProviderResolveBusinessEntityReference,
    doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails,
    doppeListActionGetDefaultListSettings,
    doppeListActionGetDefaultSearchListSettings,
    doppeListActionGetItemClickBehaviourDefaultListSettings,
    doppeListActionGetMainDetailsDefaultSettings,
    doppeListActionGetWithExternalDataSourceDefaultListSettings,
    doppeListActionItemCtaButtonsResetSettingsForLock,
    doppeListActionListItemCalculateItemButtons, doppeListActionListItemSettingsCreateDefault,
    doppeListActionListItemSettingsMigrateBaseSettings,
    doppeListActionListItemWithCtaButtonsMigration,
    doppeListActionListItemWithSectionsMigration,
    DoppeListActionSettings,
    doppeListActionSettingsFilterHiddenListItemsFromActionView,
    doppeListActionSettingsGetListItemCtaButtonsDefaultSettings,
    doppeListActionSettingsGetListItemSectionsDefaultSettings,
    doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration,
    doppeListActionTemplateSectionsSettingsDefaultValuesMigration,
    doppeListActionWithMainDetailsGetMainImage,
    doppeListActionWithMainDetailsReplaceMainImage
} from '../../client-server-common';
import type {
    DoppeServicesExternalDataSource
} from '../../client-server-common/types/doppe-services-external-data-source';
import {
    DoppeServicesExternalDataSourceType,
    doppeServicesExternalDataSourceResolveBusinessEntityReferences
} from '../../client-server-common/types/doppe-services-external-data-source';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {DoppeActionTypeHoppService} from '../doppe-action-type-hopp-service/doppe-action-type-hopp-service';
import {
    convertDoppeDtoBookingServiceToHoppServiceSettings,
    doppeActionTypeHoppServiceResolveSettingsFromBoundEntity
} from '../doppe-action-type-hopp-service/doppe-action-type-hopp-service';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionGetMediaViewDefaultSettingsMigration
} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import {
    bymoPageContextPropsResolveBusinessEntity,
    bymoPageContextPropsResolveBusinessEntityListItems
} from '../../doppe-sdk/bymo-page/bymo-page-context-props';

export namespace DoppeActionTypeHoppServices {

    export interface ServiceSettings extends
        DoppeListActionSettings.ListItemBaseSettings,
        DoppeListActionSettings.ListItemWithCtaButtons,
        DoppeListActionSettings.ListItemWithSections,
        DoppeActionTypeHoppService.BaseActionSettings {
    }

    export interface ActionSettings extends
        DoppeListActionSettings<ServiceSettings>,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings,
        DoppeListActionSettings.WithSearchSettings,
        DoppeListActionSettings.WithMainDetails,
        DoppeListActionSettings.WithItemClickBehaviour,
        DoppeListActionSettings.WithExternalDataSourceSupport<DoppeServicesExternalDataSource> {
        previewShowTitle: boolean;
        previewShowDescription: boolean;
        previewShowPrice: boolean;
        previewShowLocation: boolean;
        previewShowParticipants: boolean;
    }
}

export const doppeActionTypeHoppServices = doppeActionTypeDefineMetadata<DoppeActionTypeHoppServices.ActionSettings>({
    id: 'io.bymo.action/hopp-services',
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeListActionItemCtaButtonsResetSettingsForLock(actionSettings);
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveActionSettings: action => {

        const {
            listDataItems = [],
            layout = DoppeActionLayout.Column,
            previewShowTitle = true,
            previewShowDescription = true,
            previewShowPrice = true,
            previewShowLocation = true,
            previewShowParticipants = true,
            ...restSettings
        } = action.settings;

        return {
            ...restSettings,
            previewShowTitle: previewShowTitle,
            previewShowDescription: previewShowDescription,
            previewShowPrice: previewShowPrice,
            previewShowLocation: previewShowLocation,
            previewShowParticipants: previewShowParticipants,
            ...doppeListActionGetWithExternalDataSourceDefaultListSettings(action.settings),
            ...doppeListActionGetItemClickBehaviourDefaultListSettings(action.settings),
            ...doppeListActionGetMainDetailsDefaultSettings({}, action.settings),
            ...doppeListActionGetDefaultSearchListSettings(action.settings),
            ...doppeListActionGetDefaultListSettings({
                ...action.settings,
                layout: layout,
                listDataItems: listDataItems.map(service => {

                    const {
                        ctaButtonsOverridesMap,
                        customCtaButtons,
                        id,
                        deleted,
                        displayInSearch,
                        enabled,
                        sectionsOverridesMap,
                        customSections,
                        ...restService
                    } = service;

                    return {
                        ...doppeActionGetMediaViewDefaultSettingsMigration(service),
                        ...doppeListActionListItemWithCtaButtonsMigration(service, false),
                        ...doppeListActionListItemWithSectionsMigration(service),
                        ...doppeListActionListItemSettingsMigrateBaseSettings(service),
                        ...restService as PartiallyOptional<DoppeActionTypeHoppServices.ServiceSettings, 'mainMediaViewSettings' | 'mainMediaViewLayout'  | 'ctaButtonsOverridesMap' |'customCtaButtons' | 'sectionsOverridesMap' | 'customSections'>
                    }
                }),
            }),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration(action.settings),
        } satisfies DoppeActionTypeHoppServices.ActionSettings
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            ...doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails(),
            externalDataSourceItemsCount: BymoPagePropsSpecialContentSettingMetadata.Ignored,
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {
                [DoppeActionCTAType.BookNow]: true
            }
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                mainImage: true,
                mainTitle: true
            }
    },
    actionListItemSettingsMetadata: {
        contentSettingsMetadata: {
            serviceTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            serviceImages: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            location: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon),
            servicePageLink: BymoPagePropsSpecialContentSettingMetadata.Ignored,
            priceData: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
            servicePeriod: BymoPagePropsSpecialContentSettingMetadata.Ignored,
            serviceSchedule: BymoPagePropsSpecialContentSettingMetadata.Ignored,
            serviceParticipants: BymoPagePropsSpecialContentSettingMetadata.Ignored,
            couponCode: BymoPagePropsSpecialContentSettingMetadata.Ignored
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {
                [DoppeActionCTAType.BookNow]: true
            }
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                couponCode: true,
                description: true,
                location: true,
                priceData: true,
                ribbon: true,
                serviceParticipants: true,
                servicePeriod: true,
                serviceSchedule: true,
                serviceTitle: true
            }
    },
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    isListItemLinkOpener: true,
    supportsListLayout: true,
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues([...arrayFlatten(iterableMapToArray(actionSettings.listDataItems, (service, skip) => {
            const mediaItems = arrayRemoveNullValues(asArray(hideableValueGetVisibleValue(service.serviceImages, []) ?? []));
            return mediaItems.length > 0 ? mediaItems : skip;
        })), hideableValueIsVisibleAndNotEqualValue(actionSettings.mainImage, null) ? actionSettings.mainImage : null]);
    },
    resolveMainMedia: actionSettings => {
        const service = actionSettings.listDataItems[0];
        const mediaItems = service ? arrayRemoveNullValues(asArray(hideableValueGetVisibleValue(service.serviceImages, []) ?? [])) : [];
        return doppeListActionWithMainDetailsGetMainImage(actionSettings, mediaItems[0]);
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    isConnectedToExternalDataSource: actionSettings => {
        return !!actionSettings.externalDataSource
    },
    resolveActualActionSettings: (actionSettings, bymoPageContextProps) => {
        const {externalDataSource, externalDataSourceItemsCount} = actionSettings;
        if (externalDataSource !== null) {
            const businessEntityRef = doppeServicesExternalDataSourceResolveBusinessEntityReferences(externalDataSource);
            const externalServices = bymoPageContextPropsResolveBusinessEntityListItems(bymoPageContextProps, businessEntityRef);

            const isHoppService = externalDataSource.type === DoppeServicesExternalDataSourceType.LatestHoppServices ||
                externalDataSource.type === DoppeServicesExternalDataSourceType.HoppServicesByCategories;

            const listDataItems = arraySkipTake(externalServices, 0, externalDataSourceItemsCount).map(doppeDtoService => {
                return convertDoppeDtoBookingServiceToHoppServicesSettings(doppeDtoService, isHoppService ? DoppeExternalServiceProvider.Hopp : DoppeExternalServiceProvider.Wix);
            });

            return {
                ...actionSettings,
                listDataItems: listDataItems
            };
        }
        return actionSettings;
    },
    resolveActualListItemSettings: (listItemId, actionSettings, bymoPageContextProps) => {
        const listItem = actionSettings.listDataItems.find(item => item.id === listItemId);
        if (!listItem) return undefined;

        const {serviceProvider, serviceProviderId} = listItem;
        if (serviceProvider === null || serviceProviderId === null) return listItem;

        const boundService = bymoPageContextPropsResolveBusinessEntity(
            bymoPageContextProps,
            doppeExternalServiceProviderResolveBusinessEntityReference(serviceProvider, serviceProviderId)
        );

        if (!boundService || doppeBusinessEntityDtoIsNotFound(boundService)) return undefined;

        return {
            ...listItem,
            ...doppeActionTypeHoppServiceResolveSettingsFromBoundEntity(listItem, boundService as DoppeDtoBookingService),
        };
    },
    resolveUsedBusinessEntities: actionSettings => {
        const externalDataSource = actionSettings.externalDataSource;
        if (externalDataSource) {
            return [
                doppeServicesExternalDataSourceResolveBusinessEntityReferences(externalDataSource)
            ];
        } else {
            return iterableMapToArray(actionSettings.listDataItems, (service, skip) => {
                return service.serviceProvider !== null && service.serviceProviderId !== null ?
                    doppeExternalServiceProviderResolveBusinessEntityReference(
                        service.serviceProvider,
                        service.serviceProviderId
                    ) : skip
            });
        }
    },
    resolveListItemsCount: actionSettings => {
        return actionSettings.externalDataSource ? actionSettings.externalDataSourceItemsCount : doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.listDataItems).length;
    },
    resolveListItems: (action, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        const actionSettings = action.settings;
        return actionSettings.listDataItems.map(item => {
            // Try to resolve the service entity from business entities data
            const serviceEntity = item.serviceProviderId ? bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                {businessEntity: DoppeBusinessEntity.HoppService, id: item.serviceProviderId}
            ) : undefined;

            // Check if we have valid entity data
            const hasValidEntity = serviceEntity && !doppeBusinessEntityDtoIsNotFound(serviceEntity);

            // Resolve values from entity or fall back to stored settings
            const title = hasValidEntity
                ? (serviceEntity.name ?? hideableValueGetVisibleValue(item.serviceTitle, ''))
                : hideableValueGetVisibleValue(item.serviceTitle, '');

            const description = hasValidEntity
                ? (serviceEntity.description ?? hideableValueGetVisibleValue(item.description, ''))
                : hideableValueGetVisibleValue(item.description, '');

            const ribbon = hideableValueGetVisibleValue(item.ribbon, '');

            // Resolve image from entity or fall back to stored settings
            // WixImageInfo is a valid WixImageResource which is a WixMediaResource
            const entityImages = hasValidEntity && serviceEntity.media
                ? (serviceEntity.media as WixMediaResource[])
                : null;
            const storedImages = arrayRemoveNullValues(asArray(hideableValueGetVisibleValue(item.serviceImages, []) ?? []));
            const image = (entityImages && entityImages.length > 0 ? entityImages[0] : storedImages[0]) ?? null;

            // Calculate URL from link spec
            const url = doppeActionCtaButtonsResolveMainURL(
                doppeListActionListItemCalculateItemButtons(actionSettings.listItemTemplateCtaButtonsSettings, item),
                {
                    sourceLinkSpec: item.serviceProviderId ? {
                        type: DoppeLinkSpecType.Entity,
                        entityType: DoppeLinkSpecEntityType.Service,
                        entityId: item.serviceProviderId
                    } : null,
                    viewerLang: viewerLang,
                    bymoPageProps: bymoPageProps,
                    viewerPagesRoutesManager: viewerPagesRoutesManager,
                    bymoPageContextProps: bymoPageContextProps
                }
            ) ?? null;

            return {
                title: title,
                action: action,
                couponCode: item.couponCode ?? hideableValueCreateHidden(''),
                description: hideableValueCreateHidden(description),
                image: image,
                itemId: item.id,
                ribbon: hideableValueCreateHidden(ribbon),
                url: url,
                enabled: item.enabled,
                deleted: item.deleted,
                displayInSearch: item.displayInSearch,
            }
        });
    },
    resolveListSearchSettings: actionSettings => {
        return actionSettings.listSearchSettings;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            for (const item of actionSettings.listDataItems) {
                if (item.serviceImages) {
                    const images = item.serviceImages ?? [];
                    item.serviceImages = images.map(image => {
                        return wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap)
                    })
                }
            }

            doppeListActionWithMainDetailsReplaceMainImage(actionSettings, externalImagesUrlToImageResourceMap);
        })
    }
});

export function convertDoppeDtoBookingServiceToHoppServicesSettings(
    service: DoppeDtoBookingService,
    serviceProvider: DoppeExternalServiceProvider = DoppeExternalServiceProvider.Hopp
): DoppeActionTypeHoppServices.ServiceSettings {
    const singleServiceSettings = convertDoppeDtoBookingServiceToHoppServiceSettings({
        service: service
    });

    return {
        ...singleServiceSettings,
        serviceProvider: serviceProvider,
        ...doppeListActionListItemSettingsCreateDefault(service.id),
        ...doppeListActionSettingsGetListItemCtaButtonsDefaultSettings(),
        ...doppeListActionSettingsGetListItemSectionsDefaultSettings(),
    } satisfies DoppeActionTypeHoppServices.ServiceSettings;
} 